import { PromptModal } from '@/components'
import { Combobox } from '@/components/combobox'
import { Button, Textarea } from '@/components/ui'
import type { PromptResolve } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import React, { useState } from 'react'
import type {
  EventAnswerInput,
  EventNode,
  EventRegistrationStatus,
} from '../types.graphql'
import { EventQuestionField } from './event-question-field'

export interface EmployeeAnswer {
  user: string
  status: EventRegistrationStatus
  note: string
  answers: EventAnswerInput[]
}

interface Props {
  event: EventNode
  /** Set when an existing answer is edited; the employee is then fixed. */
  userId?: string

  onSubmit: PromptResolve<EmployeeAnswer | null>
}

type Answers = Record<string, string>

/**
 * The organiser answers for an employee: picks who, then fills in what the
 * employee would have. Picking someone who already answered starts from that
 * answer.
 */
export const EventEmployeeAnswerPrompt: React.FC<Props> = ({
  event,
  userId: initialUserId,
  onSubmit,
}) => {
  const translations = useTranslate({
    signUp: 'events.sign-up-employee',
    editAnswer: 'events.edit-answer',
    afterDeadline: 'events.sign-up-after-deadline',
    employee: 'common.employee',
    selectUser: 'common.select-user',
    answer: 'events.answer',
    attending: 'events.attending',
    declined: 'events.declined',
    note: 'common.comment',
    notePlaceholder: 'events.note-placeholder',
    save: 'common.save',
  })

  const registrations = event.registrations ?? []

  const registrationOf = (userId: string) =>
    registrations.find(registration => registration.user?.id === userId)

  const answersOf = (userId: string): Answers =>
    Object.fromEntries(
      (registrationOf(userId)?.answers ?? []).map(answer => [
        answer.question.id,
        answer.value,
      ])
    )

  const [userId, setUserId] = useState(initialUserId ?? '')
  const [status, setStatus] = useState<EventRegistrationStatus | null>(
    registrationOf(userId)?.status ?? null
  )
  const [answers, setAnswers] = useState<Answers>(answersOf(userId))
  const [note, setNote] = useState(registrationOf(userId)?.note ?? '')

  const employees = [
    ...registrations.flatMap(registration =>
      registration.user ? [registration.user] : []
    ),
    ...(event.nonResponders ?? []),
  ].sort((a, b) => a.fullName.localeCompare(b.fullName))

  function selectUser(next: string) {
    setUserId(next)
    setStatus(registrationOf(next)?.status ?? null)
    setAnswers(answersOf(next))
    setNote(registrationOf(next)?.note ?? '')
  }

  const attending = status === 'ATTENDING'
  const missingRequired = event.questions.some(
    question => question.required && !answers[question.id]
  )

  return (
    <PromptModal
      title={initialUserId ? translations.editAnswer : translations.signUp}
      submitText={translations.save}
      submitDisabled={!userId || !status || (attending && missingRequired)}
      className="w-[560px]"
      onCancel={() => onSubmit(null)}
      onSubmit={() => {
        if (!userId || !status) return
        onSubmit({
          user: userId,
          status,
          note,
          answers: event.questions.map(question => ({
            question: question.id,
            value: answers[question.id] ?? '',
          })),
        })
      }}
    >
      <div className="flex flex-col gap-4">
        {!event.registrationOpen && (
          <p className="m-0 rounded-lg bg-stone-100 px-3 py-2 text-sm text-stone-600">
            {translations.afterDeadline}
          </p>
        )}

        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase text-stone-500">
            {translations.employee}
          </span>
          <Combobox
            mode="single"
            value={userId}
            items={employees.map(employee => ({
              value: employee.id,
              label: employee.fullName,
            }))}
            disabled={!!initialUserId}
            placeholder={translations.selectUser}
            className="w-full"
            onChange={selectUser}
          />
        </div>

        <div className="flex flex-col gap-1">
          <span className="text-xs font-semibold uppercase text-stone-500">
            {translations.answer}
          </span>
          <div className="flex gap-2">
            <Button
              type="button"
              size="sm"
              variant={attending ? 'default' : 'outline'}
              disabled={!userId}
              onClick={() => setStatus('ATTENDING')}
            >
              {translations.attending}
            </Button>

            <Button
              type="button"
              size="sm"
              variant={status === 'DECLINED' ? 'default' : 'outline'}
              disabled={!userId}
              onClick={() => setStatus('DECLINED')}
            >
              {translations.declined}
            </Button>
          </div>
        </div>

        {attending && event.questions.length > 0 && (
          <div className="flex flex-col gap-3">
            {event.questions.map(question => (
              <EventQuestionField
                key={question.id}
                question={question}
                value={answers[question.id] ?? ''}
                onChange={value =>
                  setAnswers(current => ({ ...current, [question.id]: value }))
                }
              />
            ))}
          </div>
        )}

        {!!status && (
          <div className="flex flex-col gap-1">
            <span className="text-xs font-semibold uppercase text-stone-500">
              {translations.note}
            </span>
            <Textarea
              value={note}
              rows={2}
              placeholder={translations.notePlaceholder}
              onChange={setNote}
            />
          </div>
        )}
      </div>
    </PromptModal>
  )
}
